// src/modulos/Parametros/SituationSACPage.js
import React, { useState, useMemo, useCallback } from 'react';
import useFirestore from '../../hooks/useFirestore';
import usePagePermissions from '../../hooks/usePagePermissions';

// Import global components
import {
    PageHeader,
    SearchBar,
    FormModal,
    ConfirmModal,
    TableSkeleton,
    EmptyState,
    Pagination,
} from '../../components/common';

// Import utilities
import { getTimestamps } from '../../utils/timestamps';

// Page-specific styles
import styles from './Parameters.module.css';

// Constantes
const SITUATION_SAC_COLLECTION = 'parameters/data/situacao-sac';
const ITEMS_PER_PAGE = 10;

// Função auxiliar para obter classe de badge
const getBadgeClass = (situationName) => {
    const nameLower = situationName.toLowerCase();

    if (nameLower.includes('urgente') || nameLower.includes('crítico') || nameLower.includes('critico') ||
        nameLower.includes('grave') || nameLower.includes('emergência') || nameLower.includes('emergencia')) {
        return `${styles.badge} ${styles.badgeDanger}`;
    }

    if (nameLower.includes('análise') || nameLower.includes('analise') || nameLower.includes('pendente') ||
        nameLower.includes('aguardando') || nameLower.includes('em andamento') || nameLower.includes('processando') ||
        nameLower.includes('verificação') || nameLower.includes('verificacao')) {
        return `${styles.badge} ${styles.badgeWarning}`;
    }

    if (nameLower.includes('finalizado') || nameLower.includes('concluído') || nameLower.includes('concluido') ||
        nameLower.includes('resolvido') || nameLower.includes('entregue') || nameLower.includes('aprovado') ||
        nameLower.includes('fechado')) {
        return `${styles.badge} ${styles.badgeSuccess}`;
    }

    if (nameLower.includes('informação') || nameLower.includes('informacao') || nameLower.includes('consulta') ||
        nameLower.includes('dúvida') || nameLower.includes('duvida') || nameLower.includes('sugestão') ||
        nameLower.includes('sugestao') || nameLower.includes('elogio')) {
        return `${styles.badge} ${styles.badgeInfo}`;
    }

    if (nameLower.includes('cancelado') || nameLower.includes('recusado') || nameLower.includes('rejeitado') ||
        nameLower.includes('arquivado')) {
        return `${styles.badge} ${styles.badgeSecondary}`;
    }

    return `${styles.badge} ${styles.badgeDefault}`;
};

const SituationSACPage = () => {
    const can = usePagePermissions('situacao_sac');

    // Estados Locais
    const [editingItem, setEditingItem] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [nameInput, setNameInput] = useState('');
    const [involvedEmailInput, setInvolvedEmailInput] = useState('');
    const [formLoading, setFormLoading] = useState(false);
    const [formError, setFormError] = useState('');
    const [formSuccess, setFormSuccess] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, id: null, name: '' });
    const [deleteLoading, setDeleteLoading] = useState(false);

    // Hook de Dados
    const {
        data: situations,
        loading: listLoading,
        error: listError,
        addDocument,
        updateDocument,
        deleteDocument,
        refreshData
    } = useFirestore(SITUATION_SAC_COLLECTION, 'name');

    // ============================================================
    // 1. DEFINIR filteredSituations ANTES dos callbacks
    // ============================================================
    const filteredSituations = useMemo(() => {
        if (!situations) return [];
        if (!searchTerm) return situations;

        const lowerCaseSearch = searchTerm.toLowerCase();
        return situations.filter(item =>
            item.name.toLowerCase().includes(lowerCaseSearch) ||
            (item.involvedEmail && item.involvedEmail.toLowerCase().includes(lowerCaseSearch))
        );
    }, [situations, searchTerm]);

    const totalFilteredItems = filteredSituations.length;
    const totalPagesAfterFilter = Math.ceil(totalFilteredItems / ITEMS_PER_PAGE);

    const currentSituations = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        return filteredSituations.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    }, [filteredSituations, currentPage]);

    // Reset do formulário
    const resetForm = useCallback(() => {
        setEditingItem(null);
        setNameInput('');
        setInvolvedEmailInput('');
        setFormError('');
        setFormSuccess('');
    }, []);

    const handleCloseModal = useCallback(() => {
        setIsModalOpen(false);
        resetForm();
    }, [resetForm]);

    const handleNewClick = useCallback(() => {
        resetForm();
        setIsModalOpen(true);
    }, [resetForm]);

    const handleEditClick = useCallback((item) => {
        setEditingItem(item);
        setNameInput(item.name);
        setInvolvedEmailInput(item.involvedEmail || '');
        setFormError('');
        setFormSuccess('');
        setIsModalOpen(true);
    }, []);

    const handleDeleteClick = useCallback((id, name) => {
        setDeleteConfirm({ isOpen: true, id, name });
    }, []);

    // Verificar uso da situação
    const isSituationInUse = useCallback(async (situationId, situationName) => {
        const TICKETS_COLLECTION = 'sac-tickets';

        try {
            const { collection, query, where, getDocs } = await import('firebase/firestore');
            const { db } = await import('../../firebaseConfig');

            const ticketsRef = collection(db, TICKETS_COLLECTION);
            const q1 = query(ticketsRef, where('situationId', '==', situationId));
            const q2 = query(ticketsRef, where('situation', '==', situationName));

            const [snapshot1, snapshot2] = await Promise.all([getDocs(q1), getDocs(q2)]);
            const totalTickets = snapshot1.size + snapshot2.size;
            return { inUse: totalTickets > 0, count: totalTickets };
        } catch (error) {
            console.error("Erro ao verificar uso da situação:", error);
            return { inUse: false, count: 0 };
        }
    }, []);

    // Confirmar Delete
    const handleConfirmDelete = useCallback(async () => {
        const { id, name } = deleteConfirm;
        setDeleteLoading(true);

        try {
            const { inUse, count } = await isSituationInUse(id, name);

            if (inUse) {
                setFormError(`Não é possível deletar a situação "${name}". Ela está sendo utilizada em ${count} ticket(s) de SAC ativo(s).`);
                setDeleteConfirm({ isOpen: false, id: null, name: '' });
                setDeleteLoading(false);
                setTimeout(() => setFormError(''), 5000);
                return;
            }

            const result = await deleteDocument(id);

            if (result.success) {
                setFormSuccess(`Situação "${name}" deletada com sucesso.`);
                setDeleteConfirm({ isOpen: false, id: null, name: '' });

                const newTotalItems = totalFilteredItems - 1;
                const maxPage = Math.ceil(newTotalItems / ITEMS_PER_PAGE);
                if (currentPage > maxPage && maxPage > 0) {
                    setCurrentPage(maxPage);
                } else if (newTotalItems === 0) {
                    setCurrentPage(1);
                }

                setTimeout(() => setFormSuccess(''), 3000);
            } else {
                setFormError(`Erro ao deletar: ${result.error}`);
                setDeleteConfirm({ isOpen: false, id: null, name: '' });
                setTimeout(() => setFormError(''), 5000);
            }
        } catch (error) {
            setFormError('Erro ao deletar situação. Tente novamente.');
            setDeleteConfirm({ isOpen: false, id: null, name: '' });
        } finally {
            setDeleteLoading(false);
        }
    }, [deleteConfirm, isSituationInUse, deleteDocument, totalFilteredItems, currentPage]);

    // Submissão do formulário
    const handleFormSubmit = useCallback(async (e) => {
        e.preventDefault();
        setFormError('');
        setFormSuccess('');

        const trimmedName = nameInput.trim();
        const trimmedEmail = involvedEmailInput.trim();

        if (!trimmedName) {
            setFormError('O campo Situação é obrigatório.');
            return;
        }

        if (trimmedEmail) {
            const emails = trimmedEmail.split(',').map(email => email.trim());
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            const invalidEmails = emails.filter(email => email && !emailRegex.test(email));

            if (invalidEmails.length > 0) {
                setFormError(`E-mail(s) inválido(s): ${invalidEmails.join(', ')}. Verifique o formato.`);
                return;
            }
        }

        const isDuplicate = situations?.some(s =>
            s.name.toLowerCase() === trimmedName.toLowerCase() &&
            s.id !== (editingItem ? editingItem.id : null)
        );

        if (isDuplicate) {
            setFormError(`A situação "${trimmedName}" já está cadastrada.`);
            return;
        }

        const userEmail = localStorage.getItem('userEmail') || 'system';
        const timestamps = getTimestamps(editingItem, userEmail);
        const dataToSave = {
            name: trimmedName,
            involvedEmail: trimmedEmail,
            ...timestamps,
        };

        setFormLoading(true);

        try {
            let result;
            if (editingItem) {
                result = await updateDocument(editingItem.id, dataToSave);
            } else {
                result = await addDocument(dataToSave);
            }

            if (result.success) {
                setFormSuccess(editingItem ? 'Situação editada com sucesso!' : 'Situação adicionada com sucesso!');
                setTimeout(() => {
                    handleCloseModal();
                }, 1500);
            } else {
                setFormError(`Falha ao ${editingItem ? 'editar' : 'adicionar'}: ${result.error}`);
            }
        } catch (error) {
            setFormError('Erro ao salvar no banco de dados. Tente novamente.');
        } finally {
            setFormLoading(false);
        }
    }, [nameInput, involvedEmailInput, editingItem, situations, addDocument, updateDocument, handleCloseModal]);

    // Paginação
    const handlePageChange = useCallback((newPage) => {
        if (newPage >= 1 && newPage <= totalPagesAfterFilter) {
            setCurrentPage(newPage);
        }
    }, [totalPagesAfterFilter]);

    const handleSearch = useCallback((term) => {
        setSearchTerm(term);
        setCurrentPage(1);
    }, []);

    return (
        <div className={styles.pageContainer}>
            {/* Form Modal */}
            <FormModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSubmit={handleFormSubmit}
                title={editingItem ? `Editar: ${editingItem.name}` : 'Adicionar Nova Situação SAC'}
                subtitle={editingItem ? `Editando: ${editingItem.name}` : undefined}
                isLoading={formLoading}
                error={formError}
                success={formSuccess}
                submitText={editingItem ? '💾 Salvar Edição' : '✨ Adicionar Situação'}
            >
                <div className={styles.inputGroup}>
                    <label htmlFor="name">
                        Situação <span className={styles.required}>*</span>
                    </label>
                    <input
                        id="name"
                        type="text"
                        value={nameInput}
                        onChange={(e) => setNameInput(e.target.value)}
                        className={styles.input}
                        placeholder="Ex: Reclamação de Produto, Urgente, Em Análise, Finalizado"
                        required
                        disabled={formLoading}
                        autoFocus
                    />
                </div>

                <div className={styles.inputGroup}>
                    <label htmlFor="involvedEmail">E-mail dos Envolvidos (Opcional)</label>
                    <textarea
                        id="involvedEmail"
                        value={involvedEmailInput}
                        onChange={(e) => setInvolvedEmailInput(e.target.value)}
                        className={styles.textarea}
                        placeholder="email1@empresa.com, email2@empresa.com"
                        rows="3"
                        disabled={formLoading}
                    />
                    <small className={styles.fieldHint}>
                        Informe os e-mails de notificação para esta situação, separados por vírgula.
                        Exemplo: qualidade@empresa.com, sac@empresa.com, supervisor@empresa.com
                    </small>
                </div>
            </FormModal>

            {/* Delete Confirmation Modal */}
            <ConfirmModal
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, id: null, name: '' })}
                onConfirm={handleConfirmDelete}
                title="⚠️ Confirmar Exclusão"
                message={`Tem certeza que deseja deletar a situação "${deleteConfirm.name}"?\n\nEsta ação não poderá ser desfeita.`}
                isLoading={deleteLoading}
                danger={true}
            />

            {/* Page Header */}
            <PageHeader
                title="Situações SAC"
                actionText="Adicionar Nova"
                onAction={handleNewClick}
                disabled={formLoading}
                showAction={can.create}
            />

            {/* Search Bar */}
            <div className={styles.searchBar}>
                <SearchBar
                    placeholder="Buscar por Situação ou E-mail..."
                    onSearch={handleSearch}
                    disabled={listLoading || formLoading}
                />
            </div>

            {/* Error State */}
            {listError && (
                <div className={styles.errorContainer}>
                    <div className={styles.errorIcon}>⚠️</div>
                    <p className={styles.errorText}>Erro ao carregar os dados: {listError}</p>
                    <button onClick={refreshData} className={styles.retryButton}>
                        Tentar Novamente
                    </button>
                </div>
            )}

            {/* Table or Loading State */}
            {!listError && (
                <>
                    {listLoading ? (
                        <TableSkeleton columns={3} rows={ITEMS_PER_PAGE} />
                    ) : currentSituations.length === 0 ? (
                        <EmptyState
                            icon={searchTerm ? '🔍' : '📋'}
                            title={
                                searchTerm
                                    ? 'Nenhum resultado encontrado'
                                    : 'Nenhuma situação cadastrada'
                            }
                            message={
                                searchTerm
                                    ? `Nenhum resultado encontrado para "${searchTerm}"`
                                    : "Clique em 'Adicionar Nova' para começar"
                            }
                        />
                    ) : (
                        <>
                            <div className={styles.tableContainer}>
                                <table className={styles.table}>
                                    <thead>
                                        <tr>
                                            <th>Situação</th>
                                            <th>E-mail dos Envolvidos</th>
                                            <th style={{ width: '150px' }}>Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentSituations.map((item) => (
                                            <tr key={item.id} className={styles.tableRow}>
                                                <td>
                                                    <span className={getBadgeClass(item.name)}>
                                                        {item.name}
                                                    </span>
                                                </td>
                                                <td>{item.involvedEmail || 'Nenhum e-mail cadastrado'}</td>
                                                <td className={styles.actionsCell}>
                                                    {can.edit && (
                                                        <button
                                                            onClick={() => handleEditClick(item)}
                                                            className={styles.actionButton}
                                                            disabled={formLoading}
                                                        >
                                                            Editar
                                                        </button>
                                                    )}
                                                    {can.delete && (
                                                        <button
                                                            onClick={() => handleDeleteClick(item.id, item.name)}
                                                            className={`${styles.actionButton} ${styles.deleteButton}`}
                                                            disabled={formLoading}
                                                        >
                                                            Deletar
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPagesAfterFilter}
                                totalItems={totalFilteredItems}
                                onPageChange={handlePageChange}
                                disabled={formLoading}
                                itemsLabel="situações"
                            />
                        </>
                    )}
                </>
            )}
        </div>
    );
};

export default SituationSACPage;